import { FC, ReactNode } from 'react';
import { shallow } from 'zustand/shallow';

import { Button } from 'src/general/components/Button/Button';
import { formatNumber } from 'src/general/helpers/formatNumber';
import { CarDisplayState } from 'src/pdp/sections/Pdp/pdpHelpers';
import { baseCarDataToDataLayerItem, pushToDataLayer } from 'src/utils/pushToDataLayer';

import { pdpStore } from 'src/stores/pdpStore';
import { BaseCarData } from 'src/types/Car.types';
import { PRICE_CARD_IDS } from '../CarPriceCard/CarPriceCard';
import { CarPriceCardDetails } from '../CarPriceCard/CarPriceCardDetails';
import styles from './CarStickyBar.module.scss';

interface Props {
  displayState: CarDisplayState;
  isCheckoutProgress: boolean;
  carData: BaseCarData;
  microcopy: {
    startCheckout: ReactNode;
    continueCheckout: ReactNode;
    notAvailable: ReactNode;
    message: ReactNode;
    financeData: ReactNode;
    financeDataMobile: ReactNode;
  };
}

export const CarStickyBarContent: FC<Props> = ({ isCheckoutProgress, displayState, carData, microcopy }) => {
  const { setCurrentModal } = pdpStore(
    ({ setCurrentModal }) => ({
      setCurrentModal,
    }),
    shallow,
  );
  const href = isCheckoutProgress ? `/checkout?sku=${carData.sku}` : `/product-detail/${carData.sku}/get-started`;

  const onOpenCarEnquiryForm = () => {
    setCurrentModal({ name: 'enquiry' });
    pushToDataLayer({
      event: 'lead_form_start',
      ecommerce: {
        items: [baseCarDataToDataLayerItem(carData)],
      },
      entry_point: 'sticky_cta',
    });
  };

  return (
    <div className={styles.content}>
      <header className={styles.carData}>
        <h2 className={styles.title}>{carData.model}</h2>
        <CarPriceCardDetails carData={carData} className={styles.carDataList} />
      </header>
      <div className={styles.price} aria-describedby={PRICE_CARD_IDS.PRICE}>
        {formatNumber(carData.price, { prepend: '$', append: '*' })}
      </div>
      <div className={styles.financePrice} aria-describedby={PRICE_CARD_IDS.FINANCE}>
        {microcopy.financeData}
      </div>
      <div className={styles.financePriceMobile} aria-describedby={PRICE_CARD_IDS.FINANCE}>
        {microcopy.financeDataMobile}
      </div>
      <div className={styles.controls}>
        {displayState === 'not-available' ? (
          <Button
            variant="primary"
            href={`/product-detail/${carData.sku}/similar`}
            data-testid="carStickyBarSimilarCars"
          >
            {microcopy.notAvailable}
          </Button>
        ) : (
          <>
            <Button
              href={displayState === 'loading' ? undefined : href}
              isLoading={displayState === 'loading'}
              isDisabledWhileLoading
              variant="accent"
            >
              {displayState === 'continue-purchase' ? microcopy.continueCheckout : microcopy.startCheckout}
            </Button>
            <Button variant="secondary" onClick={onOpenCarEnquiryForm} data-testid="carStickyBarMessage">
              {microcopy.message}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
